<template>
    <DataTable :value="processedData" :scrollable="true" scrollDirection="both" class="p-datatable-gridlines">
        <Column field="name" header="Nome" :frozen="true" style="width:250px; font-weight:bold"></Column>
        <Column v-for="month in months" :key="month.value" :field="month.field" style="width:100px; text-align:center">
            <template #header>
                <div style="width: 100%; text-align: center;" class="font-bold">
                    {{ month.label }}
                </div>
            </template>
            <template #body="{data}">
                <div style="padding: 1rem; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
                    <span :class="{'text-gray-400': !data[month.field]}" style="font-weight: bold;">
                        {{ data[month.field] || 0 }}
                    </span>
                </div>
            </template>
        </Column>
    </DataTable>
</template>

<script>
import { computed } from 'vue';

export default {
    name: 'AttendanceSummaryTable',
    props: {
        data: {
            type: [Array, Object],
            required: true
        },
        type: {
            type: String, // 'athlete' or 'staff'
            required: true
        }
    },
    emits: ['cell-click', 'header-click'],
    setup(props, { emit }) {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        
        let startYear = currentYear;
        if (currentMonth < 8) {
            startYear--;
        }
        const nextYear = startYear + 1;

        const months = [
            { label: `Agosto ${startYear}`, value: 8, field: 'month_8' },
            { label: `Settembre ${startYear}`, value: 9, field: 'month_9' },
            { label: `Ottobre ${startYear}`, value: 10, field: 'month_10' },
            { label: `Novembre ${startYear}`, value: 11, field: 'month_11' },
            { label: `Dicembre ${startYear}`, value: 12, field: 'month_12' },
            { label: `Gennaio ${nextYear}`, value: 1, field: 'month_1' },
            { label: `Febbraio ${nextYear}`, value: 2, field: 'month_2' },
            { label: `Marzo ${nextYear}`, value: 3, field: 'month_3' },
            { label: `Aprile ${nextYear}`, value: 4, field: 'month_4' },
            { label: `Maggio ${nextYear}`, value: 5, field: 'month_5' },
            { label: `Giugno ${nextYear}`, value: 6, field: 'month_6' },
            { label: `Luglio ${nextYear}`, value: 7, field: 'month_7' }
        ];

        const onHeaderClick = (month) => {
            const year = parseInt(month.label.split(' ')[1]);
            emit('header-click', {
                month: month.value,
                year: year,
                type: props.type
            });
        };

        const onCellClick = (row, month) => {
            const year = parseInt(month.label.split(' ')[1]);
            emit('cell-click', {
                id: row.id,
                name: row.name,
                month: month.value,
                year: year,
                type: props.type
            });
        };

        const processedData = computed(() => {
            if (!props.data) return [];
            
            if (props.type === 'athlete') {
                if (!Array.isArray(props.data)) return [];
                
                // Group by course
                const courses = {};
                props.data.forEach(item => {
                    if (!courses[item.corso_id]) {
                        courses[item.corso_id] = {
                            name: item.corso_nome,
                            id: item.corso_id
                        };
                    }
                    courses[item.corso_id][`month_${item.mese}`] = item.presenze;
                });
                return Object.values(courses).sort((a, b) => a.name.localeCompare(b.name));
            } else {
                // Staff logic
                if (!props.data.staffs) return [];
                
                const staffMap = {};
                
                // Helper to get or create a staff-course entry
                const getEntry = (staffId, courseId, courseName) => {
                    const staff = props.data.staffs.find(s => s.id === staffId);
                    if (!staff) return null;
                    
                    const key = `${staffId}_${courseId || 'total'}`;
                    if (!staffMap[key]) {
                        staffMap[key] = {
                            name: courseName ? `${staff.cognome} ${staff.nome} (${courseName})` : `${staff.cognome} ${staff.nome}`,
                            id: staffId,
                            course_id: courseId
                        };
                    }
                    return staffMap[key];
                };

                // Process assigned
                if (props.data.assigned) {
                    props.data.assigned.forEach(item => {
                        const entry = getEntry(item.id_personale, item.corso_id, item.corso_nome);
                        if (entry) {
                            const monthKey = `month_${item.mese}`;
                            entry[monthKey] = (entry[monthKey] || 0) + item.count;
                        }
                    });
                }
                
                // Process absences (subtract)
                if (props.data.absences) {
                    props.data.absences.forEach(item => {
                        const entry = getEntry(item.id_personale, item.id_corso, item.corso_nome);
                        if (entry) {
                            const monthKey = `month_${item.mese}`;
                            entry[monthKey] = (entry[monthKey] || 0) - item.count;
                        }
                    });
                }
                
                // Process substitutions (add)
                if (props.data.substitutions) {
                    props.data.substitutions.forEach(item => {
                        const entry = getEntry(item.id_personale, item.id_corso, item.corso_nome);
                        if (entry) {
                            const monthKey = `month_${item.mese}`;
                            entry[monthKey] = (entry[monthKey] || 0) + item.count;
                        }
                    });
                }
                
                return Object.values(staffMap).sort((a, b) => a.name.localeCompare(b.name));
            }
        });

        return {
            months,
            processedData,
            onHeaderClick,
            onCellClick
        };
    }
}
</script>

<style scoped>
.text-gray-400 {
    color: #ccc;
}
</style>
